import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import CarCard from '../components/CarCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, SlidersHorizontal, X, Car, Bookmark } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../LanguageContext';

const CarsPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    brand: searchParams.get('brand') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    minYear: searchParams.get('minYear') || '',
    maxYear: searchParams.get('maxYear') || '',
    condition: searchParams.get('condition') || '',
    bodyType: searchParams.get('bodyType') || '',
    fuelType: searchParams.get('fuelType') || '',
    transmission: searchParams.get('transmission') || '',
  });

  const [searchQuery, setSearchQuery] = useState('');

  const brands = ['BMW', 'Mercedes-Benz', 'Audi', 'Tesla', 'Porsche', 'Ferrari', 'Lamborghini', 'Toyota', 'Honda', 'Ford', 'Chevrolet', 'Volkswagen'];
  const bodyTypes = ['Sedan', 'SUV', 'Coupe', 'Wagon', 'Convertible', 'Hatchback'];
  const fuelTypes = ['Petrol', 'Diesel', 'Electric', 'Hybrid'];
  const transmissions = ['Automatic', 'Manual'];
  const conditions = ['new', 'used'];
  const years = Array.from({ length: 30 }, (_, i) => 2026 - i);

  useEffect(() => {
    fetchCars();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user || token) {
      axios.get(`${API}/favorites/ids`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
        .then(r => setFavoriteIds(r.data)).catch(() => {});
    }
  }, [token, user]);

  const handleFavToggle = (carId, favorited) => {
    setFavoriteIds(prev => favorited ? [...prev, carId] : prev.filter(id => id !== carId));
  };

  const saveSearch = async () => {
    if (!user && !token) { toast.error(t('cars.loginSaveSearch')); return; }
    const activeF = Object.fromEntries(Object.entries(filters).filter(([,v]) => v));
    if (Object.keys(activeF).length === 0 && !searchQuery) { toast.error(t('cars.applyFilters')); return; }
    const name = [activeF.brand, activeF.bodyType, activeF.fuelType, searchQuery].filter(Boolean).join(' ') || 'All Cars';
    try {
      await axios.post(`${API}/saved-searches`, { name, filters: { ...activeF, search: searchQuery } }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      toast.success(t('cars.searchSaved'));
    } catch { toast.error(t('cars.saveFailed')); }
  };

  const fetchCars = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          // Convert camelCase to snake_case for API
          const apiKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
          params.append(apiKey, value);
        }
      });
      
      const response = await axios.get(`${API}/cars?${params.toString()}`);
      setCars(response.data);
    } catch (error) {
      console.error('Error fetching cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    // Convert "all" and "any" placeholder values back to empty string
    const actualValue = (value === 'all' || value === 'any') ? '' : value;
    const newFilters = { ...filters, [key]: actualValue };
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      brand: '',
      minPrice: '',
      maxPrice: '',
      minYear: '',
      maxYear: '',
      condition: '',
      bodyType: '',
      fuelType: '',
      transmission: '',
    });
    setSearchParams({});
  };

  const filteredCars = searchQuery 
    ? cars.filter(car => 
        car.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        car.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
        car.model.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : cars;

  const activeFiltersCount = Object.values(filters).filter(v => v).length;

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-5xl font-bold uppercase mb-4">
            {t('cars.title1')} <span className="text-cyan-500 neon-text">{t('cars.title2')}</span>
          </h1>
          <p className="text-gray-400">
            {loading ? t('cars.loading') : `${filteredCars.length} ${t('cars.available')}`}
          </p>
        </div>

        {/* Search & Filter Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              type="text"
              placeholder={t('cars.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-black/50 border-white/10 text-white placeholder:text-gray-600 focus:border-cyan-500"
              data-testid="search-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={`h-12 px-6 border-white/10 ${showFilters || activeFiltersCount > 0 ? 'border-cyan-500/50 text-cyan-500' : ''}`}
            data-testid="toggle-filters-btn"
          >
            <SlidersHorizontal className="w-5 h-5 mr-2" />
            {t('cars.filters')}
            {activeFiltersCount > 0 && (
              <span className="ml-2 w-6 h-6 rounded-full bg-cyan-500 text-black text-sm flex items-center justify-center font-bold">
                {activeFiltersCount}
              </span>
            )}
          </Button>
          {token && (
            <Button variant="outline" onClick={saveSearch} className="h-12 px-4 border-white/10 hover:border-cyan-500/50 hover:text-cyan-500" data-testid="save-search-btn">
              <Bookmark className="w-5 h-5" />
            </Button>
          )}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="glass-card rounded-xl p-6 mb-8 animate-slide-up">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg font-semibold">{t('cars.filtersTitle')}</h3>
              {activeFiltersCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearFilters}
                  className="text-gray-400 hover:text-white"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  {t('cars.clearAll')}
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Brand */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">{t('cars.brand')}</label>
                <Select value={filters.brand} onValueChange={(v) => handleFilterChange('brand', v)}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="brand-filter">
                    <SelectValue placeholder={t('cars.allBrands')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10">
                    <SelectItem value="all">{t('cars.allBrands')}</SelectItem>
                    {brands.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Body Type */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">{t('cars.bodyType')}</label>
                <Select value={filters.bodyType} onValueChange={(v) => handleFilterChange('bodyType', v)}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="body-type-filter">
                    <SelectValue placeholder={t('cars.allTypes')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10">
                    <SelectItem value="all">{t('cars.allTypes')}</SelectItem>
                    {bodyTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Fuel Type */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">{t('cars.fuelType')}</label>
                <Select value={filters.fuelType} onValueChange={(v) => handleFilterChange('fuelType', v)}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="fuel-type-filter">
                    <SelectValue placeholder={t('cars.allFuels')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10">
                    <SelectItem value="all">{t('cars.allFuels')}</SelectItem>
                    {fuelTypes.map(fuel => (
                      <SelectItem key={fuel} value={fuel}>{fuel}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Transmission */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">{t('cars.transmission')}</label>
                <Select value={filters.transmission} onValueChange={(v) => handleFilterChange('transmission', v)}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="transmission-filter">
                    <SelectValue placeholder={t('cars.all')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10">
                    <SelectItem value="all">{t('cars.all')}</SelectItem>
                    {transmissions.map(trans => (
                      <SelectItem key={trans} value={trans}>{trans}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Condition */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">{t('cars.condition')}</label>
                <Select value={filters.condition} onValueChange={(v) => handleFilterChange('condition', v)}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="condition-filter">
                    <SelectValue placeholder={t('cars.all')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10">
                    <SelectItem value="all">{t('cars.all')}</SelectItem>
                    {conditions.map(cond => (
                      <SelectItem key={cond} value={cond}>{cond === 'new' ? t('cars.new') : t('cars.used')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Min Year */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">{t('cars.minYear')}</label>
                <Select value={filters.minYear} onValueChange={(v) => handleFilterChange('minYear', v)}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="min-year-filter">
                    <SelectValue placeholder={t('cars.any')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 max-h-60">
                    <SelectItem value="any">{t('cars.any')}</SelectItem>
                    {years.map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Price Range */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">{t('cars.minPrice')}</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                  className="bg-black/50 border-white/10"
                  data-testid="min-price-filter"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-2 block">{t('cars.maxPrice')}</label>
                <Input
                  type="number"
                  placeholder={t('cars.any')}
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                  className="bg-black/50 border-white/10"
                  data-testid="max-price-filter"
                />
              </div>
            </div>
          </div>
        )}

        {/* Cars Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-xl bg-white/5 aspect-[4/5] animate-pulse"></div>
            ))}
          </div>
        ) : filteredCars.length === 0 ? (
          <div className="text-center py-20">
            <Car className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">{t('cars.noCars')}</h3>
            <p className="text-gray-400 mb-6">{t('cars.noCarsHint')}</p>
            <Button onClick={clearFilters} className="bg-cyan-500 text-black hover:bg-cyan-400">
              {t('cars.clearFilters')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6" data-testid="cars-grid">
            {filteredCars.map((car) => (
              <CarCard key={car.id} car={car} favoriteIds={favoriteIds} onFavToggle={handleFavToggle} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CarsPage;
